/**
 * Lightweight analytics/ads tracking helper.
 *
 * Installs Google Ads (gtag.js) and Meta (Facebook) Pixel only when the
 * corresponding environment variables are set. This keeps the dev build clean
 * and prevents the scripts from loading for local development.
 *
 * Required env variables (set in frontend/.env or deployment env):
 *   REACT_APP_GOOGLE_ADS_ID          - e.g. "AW-1234567890"
 *   REACT_APP_GOOGLE_ADS_CONVERSION  - full conversion label, e.g. "AW-1234567890/abc123" (for form submit)
 *   REACT_APP_META_PIXEL_ID          - e.g. "1234567890123456"
 *
 * Usage from components:
 *   import { trackLead, trackPhoneClick, trackAdClick } from '@/lib/analytics';
 *   trackLead();                  // form submission
 *   trackPhoneClick();            // "click to call" on tel: link
 *   trackAdClick('cta_hero');     // any CTA button
 */

const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const GOOGLE_ADS_CONVERSION = process.env.REACT_APP_GOOGLE_ADS_CONVERSION || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

let initialized = false;

function loadScript(src, attrs = {}) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Initialize gtag and Meta Pixel. Idempotent - safe to call multiple times.
 * Called once from index.js at boot.
 */
export function initAnalytics() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // --- Google Ads gtag.js ---
  if (GOOGLE_ADS_ID) {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line func-names
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ADS_ID);
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`).catch(() => {
      // fail silently - don't break the app if ad blockers kill the script
    });
  }

  // --- Meta (Facebook) Pixel ---
  if (META_PIXEL_ID) {
    // Standard Meta Pixel bootstrap snippet, adapted to no-globals.
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    if (window.fbq) {
      window.fbq('init', META_PIXEL_ID);
      window.fbq('track', 'PageView');
    }
  }
}

/**
 * Track a client-side SPA navigation (called from ScrollToTop effect).
 */
export function trackPageView(pathname) {
  if (typeof window === 'undefined') return;
  if (GOOGLE_ADS_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: pathname,
    });
  }
  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}

/**
 * Track a lead (form submission). Fires Google Ads conversion + Meta Lead.
 */
export function trackLead(data = {}) {
  if (typeof window === 'undefined') return;
  if (GOOGLE_ADS_ID && typeof window.gtag === 'function') {
    if (GOOGLE_ADS_CONVERSION) {
      window.gtag('event', 'conversion', {
        send_to: GOOGLE_ADS_CONVERSION,
      });
    }
    window.gtag('event', 'generate_lead', {
      event_category: 'form',
      ...data,
    });
  }
  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', data);
  }
}

/**
 * Track a phone click (tel: link).
 */
export function trackPhoneClick() {
  if (typeof window === 'undefined') return;
  if (GOOGLE_ADS_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'click_phone', {
      event_category: 'contact',
    });
  }
  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'Contact');
  }
}

/**
 * Track a CTA / button / link click by name (e.g. "hero_primary").
 */
export function trackAdClick(name) {
  if (typeof window === 'undefined') return;
  if (GOOGLE_ADS_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'click_cta', {
      event_category: 'cta',
      event_label: name,
    });
  }
  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'CTAClick', { name });
  }
}

/**
 * Track an important page view (pricing, portfolio) as a Meta ViewContent event.
 */
export function trackViewContent(name) {
  if (typeof window === 'undefined') return;
  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'ViewContent', { content_name: name });
  }
}

/**
 * Read helper - whether analytics is configured at all.
 */
export const analyticsEnabled = Boolean(GOOGLE_ADS_ID || META_PIXEL_ID);
